// Pure types and helpers shared by the client workspace and its tabs.
// Moved out of the old Client.tsx so every tab reads the same rules.
import { vendorLabel, type VendorLabelSource } from "../../lib/friendly-labels";

export interface ClientOrg {
  id: string;
  name: string;
}

export interface ClientEnvironment {
  name: string;
  disabled?: boolean;
}

interface InstanceRef {
  id?: string;
  name?: string;
  connectorType: string;
}

export interface ClientConnection {
  id: string;
  name: string;
  connectorType: string;
  /** null/undefined = any environment (shared); otherwise scoped to one. */
  environment?: string | null;
}

export interface ClientPipeline {
  id: string;
  name: string;
  enabled?: boolean;
  disabled?: boolean;
  environment?: string;
  transformId?: string;
  triggers?: Array<{ mode?: string }>;
  sourceInstance?: InstanceRef | null;
  destInstance?: InstanceRef | null;
}

/** Active when explicitly enabled, or when neither flag says otherwise. */
export function isActive(p: ClientPipeline): boolean {
  return p.enabled === true || (p.enabled == null && p.disabled !== true);
}

/** "Jump → HubSpot" in plain vendor names. */
export function flowLabel(p: ClientPipeline, connectors?: readonly VendorLabelSource[]): string {
  return `${vendorLabel(p.sourceInstance?.connectorType, connectors)} → ${vendorLabel(p.destInstance?.connectorType, connectors)}`;
}

/** Any-env connections are shared, so they stay visible alongside the scoped ones. */
export function filterConnectionsForEnv(
  connections: ClientConnection[],
  env: string | null | undefined,
): ClientConnection[] {
  if (!env) return connections;
  return connections.filter((c) => c.environment == null || c.environment === env);
}

/** A pipeline carries exactly one environment, so scoping is an exact match. */
export function filterPipelinesForEnv(
  pipelines: ClientPipeline[],
  env: string | null | undefined,
): ClientPipeline[] {
  if (!env) return pipelines;
  return pipelines.filter((p) => p.environment === env);
}

/** "1 pipeline" / "4 pipelines" — the count and its noun, agreeing. */
function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "4 pipelines · 3 active · 3 connections" (spec §3.3). */
export function summaryLine(pipelines: ClientPipeline[], connections: ClientConnection[]): string {
  const active = pipelines.filter(isActive).length;
  return `${plural(pipelines.length, "pipeline", "pipelines")} · ${active} active · ${plural(connections.length, "connection", "connections")}`;
}

export interface EnvOption {
  value: string;
  label: string;
}

/**
 * Environment choices for a selector, with the current value always present.
 *
 * WHY THE CURRENT VALUE IS APPENDED: a paused environment is filtered out of
 * the list, but nothing removes it from `?env=` or from a connection's stored
 * `environment`. A controlled selector then holds a value none of its options
 * carry — the segmented control shows nothing active, a native `<select>` may
 * fall back to its first option without saying so, and the filtering underneath
 * carries on using the value the operator can no longer see. Worse, when every
 * environment is paused the selector is hidden entirely and the stale value
 * becomes unclearable.
 *
 * So the current value stays on the list, marked, and selectable — the operator
 * can see what is filtering their view and choose their way out of it.
 */
export function envOptions(
  environments: ClientEnvironment[],
  current: string | null | undefined,
  allLabel: string,
): EnvOption[] {
  const options: EnvOption[] = [
    { value: "", label: allLabel },
    ...environments.filter((e) => !e.disabled).map((e) => ({ value: e.name, label: e.name })),
  ];
  if (!current || options.some((o) => o.value === current)) return options;
  const known = environments.find((e) => e.name === current);
  return [...options, { value: current, label: `${current} (${known ? "paused" : "unavailable"})` }];
}
